import { useEffect, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { CircleAlert, Lock } from "lucide-react";
import { t } from "../i18n/index.ts";
import { ApiError } from "../api/client.ts";
import css from "./ui.module.css";
import { Button } from "./Button.tsx";
import { Icon } from "./Icon.tsx";

/** 300 ms altında hiçbir şey göstermez (titreme olmasın) — iskelet için (§6.18). */
export function useDelayed(active: boolean, ms = 300): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!active) return setOn(false);
    const h = setTimeout(() => setOn(true), ms);
    return () => clearTimeout(h);
  }, [active, ms]);
  return on;
}

export function Skeleton({ w = "70%", h = "var(--hk-font-line-height-base)", round, style }: { w?: string; h?: string; round?: boolean; style?: React.CSSProperties }) {
  return <div className={css.skeleton} aria-hidden="true" style={{ width: w, height: h, borderRadius: round ? "var(--hk-radius-full)" : undefined, ...style }} />;
}

/** Satırlardan oluşan iskelet listesi; yükleme 300 ms'yi aşarsa görünür. */
export function SkeletonRows({ rows = 6, active = true, avatar }: { rows?: number; active?: boolean; avatar?: boolean }) {
  const show = useDelayed(active);
  if (!show) return <div aria-busy="true" />;
  const widths = ["80%", "60%", "72%", "50%", "88%", "66%"];
  return (
    <div aria-busy="true" aria-live="polite" style={{ display: "flex", flexDirection: "column", gap: "var(--hk-space-3)", padding: "var(--hk-space-4)" }}>
      <span className="sr-only">{t("common.loading")}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} style={{ display: "flex", gap: "var(--hk-space-3)", alignItems: "center" }}>
          {avatar && <Skeleton w="var(--hk-size-avatar-md)" h="var(--hk-size-avatar-md)" round />}
          <Skeleton w={widths[i % widths.length]} />
        </div>
      ))}
    </div>
  );
}

export function Empty({ icon, text, action }: { icon: LucideIcon; text: string; action?: ReactNode }) {
  return (
    <div className={css.empty}>
      <Icon icon={icon} size="lg" />
      <p>{text}</p>
      {action}
    </div>
  );
}

export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "NETWORK") return t("error.network");
    if (e.status === 403) return t("error.forbidden");
    if (e.status === 429) return t("error.rateLimited");
    return e.message || t("error.generic");
  }
  return t("error.generic");
}

/** Alan içi hata kartı + "Yeniden dene" (§6.18); sayfayı kapatmaz. */
export function ErrorCard({ error, onRetry }: { error?: unknown; onRetry?: () => void }) {
  return (
    <div className={css.errorCard} role="alert">
      <Icon icon={CircleAlert} />
      <div className={css.errorBody}>
        <p>{error ? errorText(error) : t("error.generic")}</p>
        {onRetry && (
          <Button size="sm" onClick={onRetry}>
            {t("common.retry")}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Yetkisiz doğrudan bağlantı kartı. */
export function NoAccessCard({ text, onBack }: { text: string; onBack?: () => void }) {
  return (
    <div className={css.lockCard}>
      <Icon icon={Lock} size="lg" />
      <p>{text}</p>
      {onBack && <Button onClick={onBack}>{t("common.back")}</Button>}
    </div>
  );
}
